import React, { useState, useEffect, useCallback } from 'react'
import { videoSettingsApi } from '../../services/videoSettings'
import type { VideoSetting } from '../../services/videoSettings'
import { Toast } from '../shared/Toast'
import { ConfirmDialog } from '../shared/ConfirmDialog'

// YouTube play button icon
function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

export function VideoSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<VideoSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await videoSettingsApi.show()
      setSetting(data)
    } catch {
      setError('Failed to load video settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleProviderChange = async (provider: string) => {
    if (!setting) return
    if (provider === setting.provider) return

    try {
      setSaving(true)
      const updated = await videoSettingsApi.update({ provider })
      setSetting(updated)
      showToast('Video provider updated', 'success')
    } catch {
      showToast('Failed to update provider', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleAutoUploadToggle = async () => {
    if (!setting) return

    try {
      setSaving(true)
      const updated = await videoSettingsApi.update({ auto_upload_enabled: !setting.auto_upload_enabled })
      setSetting(updated)
      showToast(updated.auto_upload_enabled ? 'Auto-upload enabled' : 'Auto-upload disabled', 'success')
    } catch {
      showToast('Failed to update auto-upload setting', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    setShowDisconnectConfirm(false)
    try {
      setSaving(true)
      const updated = await videoSettingsApi.disconnectYoutube()
      setSetting(updated)
      showToast('YouTube disconnected successfully', 'success')
    } catch {
      showToast('Failed to disconnect YouTube', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleConnectYouTube = () => {
    // TODO: Implement YouTube OAuth flow (VMS-199)
    // This will open a popup for Google OAuth and handle the callback
    showToast('YouTube OAuth integration coming soon', 'error')
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading video settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  const isYouTubeConnected = setting.youtube_connected
  const isYouTubeProvider = setting.provider === 'youtube'

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {showDisconnectConfirm && (
        <ConfirmDialog
          title="Disconnect YouTube"
          message="This will remove your YouTube connection and clear all saved tokens. You'll need to reconnect to upload videos. Are you sure?"
          confirmLabel="Disconnect"
          onConfirm={handleDisconnect}
          onCancel={() => setShowDisconnectConfirm(false)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-secondary">Video Settings</h1>
        <p className="text-sm text-grey mt-1">Connect a video provider to manage vehicle walkthrough videos.</p>
      </div>

      {/* Provider Selection */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Video Provider</h2>
          <p className="text-xs text-grey mt-0.5">Select how you want to host your vehicle videos.</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            {/* No Provider */}
            <button
              onClick={() => handleProviderChange('no_provider')}
              disabled={saving}
              className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                !isYouTubeProvider
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {!isYouTubeProvider && (
                <div className="absolute top-2.5 right-2.5">
                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
              )}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                !isYouTubeProvider ? 'bg-primary/10' : 'bg-gray-100'
              }`}>
                <svg className={`w-5 h-5 ${!isYouTubeProvider ? 'text-primary' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <div className="text-center">
                <span className={`text-sm font-semibold ${!isYouTubeProvider ? 'text-secondary' : 'text-gray-600'}`}>None</span>
                <p className="text-xs text-grey mt-0.5">No video provider</p>
              </div>
            </button>

            {/* YouTube */}
            <button
              onClick={() => handleProviderChange('youtube')}
              disabled={saving}
              className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all ${
                isYouTubeProvider
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              } ${saving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {isYouTubeProvider && (
                <div className="absolute top-2.5 right-2.5">
                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
              )}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isYouTubeProvider ? 'bg-red-50' : 'bg-gray-100'
              }`}>
                <YouTubeIcon className={`w-5 h-5 ${isYouTubeProvider ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
              <div className="text-center">
                <span className={`text-sm font-semibold ${isYouTubeProvider ? 'text-secondary' : 'text-gray-600'}`}>YouTube</span>
                <p className="text-xs text-grey mt-0.5">Google YouTube</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* YouTube Connection Card — only shown when YouTube is the selected provider */}
      {isYouTubeProvider && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-secondary">YouTube Connection</h2>
            <p className="text-xs text-grey mt-0.5">Manage your YouTube account connection for video uploads.</p>
          </div>

          {isYouTubeConnected ? (
            /* Connected State */
            <div className="p-6 space-y-6">
              {/* Channel info */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <YouTubeIcon className="w-6 h-6 text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-secondary truncate">{setting.youtube_channel_name}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      Connected
                    </span>
                  </div>
                  {setting.youtube_channel_id && (
                    <p className="text-xs text-grey mt-0.5">Channel ID: {setting.youtube_channel_id}</p>
                  )}
                </div>
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  disabled={saving}
                  className="shrink-0 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  Disconnect
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Auto-upload toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-secondary">Auto-upload Videos</label>
                  <p className="text-xs text-grey mt-0.5">Automatically upload new vehicle videos to your YouTube channel.</p>
                </div>
                <button
                  onClick={handleAutoUploadToggle}
                  disabled={saving}
                  role="switch"
                  aria-checked={setting.auto_upload_enabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                    setting.auto_upload_enabled ? 'bg-primary' : 'bg-gray-300'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    setting.auto_upload_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          ) : (
            /* Disconnected State */
            <div className="p-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <YouTubeIcon className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-sm font-semibold text-secondary mb-1">Connect Your YouTube Channel</h3>
                <p className="text-xs text-grey max-w-sm mx-auto mb-6">
                  Link your YouTube account to automatically upload and manage vehicle walkthrough videos.
                </p>
                <button
                  onClick={handleConnectYouTube}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <YouTubeIcon className="w-4 h-4" />
                  Connect YouTube Account
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auto-upload toggle for no_provider — hidden since no provider is selected */}
      {!isYouTubeProvider && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-secondary">Upload Settings</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between opacity-50">
              <div>
                <label className="text-sm font-medium text-secondary">Auto-upload Videos</label>
                <p className="text-xs text-grey mt-0.5">Select a video provider to enable auto-upload.</p>
              </div>
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed">
                <span className="inline-block h-4 w-4 rounded-full bg-white shadow-sm translate-x-1" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
